import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { BellRing } from "lucide-react";
import { Reveal } from "./Reveal";
import { NOTIFICATIONS } from "@/data/exceptionHub";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const KIND = {
  high: { ring: "bg-priority-bg text-priority-text", dot: "bg-priority-text" },
  done: { ring: "bg-done-bg text-done-text", dot: "bg-done-text" },
  info: { ring: "bg-brand-soft text-brand", dot: "bg-brand" },
};

export const SmartNotifications = () => {
  return (
    <section id="notifications" className="relative py-24 md:py-32 bg-canvas-soft overflow-hidden" data-testid="section-notifications">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <Reveal><span className="text-xs font-semibold uppercase tracking-[0.15em] text-brand">Smart Notifications</span></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-navy tracking-tight leading-tight text-balance">
                Relevant notifications only. No unnecessary noise.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg text-ink-500 leading-relaxed">
                Exception Hub™ notifies your team about the events that require action — the way premium productivity software should. Never another firehose of alerts.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-white border border-slate-200 px-4 py-2.5 shadow-soft">
                <BellRing className="h-4 w-4 text-brand" strokeWidth={1.75} />
                <span className="text-sm text-ink-500">Signal, not noise</span>
              </div>
            </Reveal>
          </div>

          <motion.div variants={stagger(0.08)} initial="hidden" whileInView="show" viewport={viewport} className="lg:col-span-8 space-y-3">
            {NOTIFICATIONS.map((n) => {
              const Icon = Icons[n.icon] || Icons.Bell;
              const style = KIND[n.kind] || KIND.info;
              return (
                <motion.div key={n.title} variants={fadeUp} whileHover={{ x: 6 }} transition={{ type: "spring", stiffness: 300, damping: 26 }}
                  data-testid={`notification-${n.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group flex items-center gap-4 rounded-2xl bg-white border border-slate-100 shadow-soft hover:shadow-card transition-shadow p-4 md:p-5">
                  <span className={`grid place-items-center h-11 w-11 rounded-xl shrink-0 ${style.ring}`}><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                      <p className="font-semibold text-navy truncate">{n.title}</p>
                    </div>
                    <p className="text-sm text-ink-500 mt-0.5 truncate">{n.detail}</p>
                  </div>
                  <span className="text-xs text-ink-300 shrink-0 whitespace-nowrap">{n.time}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
